import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Plus, BookOpen, User, Compass, Store, Package, ShoppingBag, Users, Megaphone, Leaf, LogOut, Settings, ChevronDown, X, Receipt, Refrigerator, UtensilsCrossed, Wine, Calendar, Trophy, CalendarPlus, Medal, Star, ClipboardList, UserPlus } from "lucide-react";
import { useAuth } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import ChefAvatar from "@/components/ChefAvatar";
import NotificationBell from "@/components/NotificationBell";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import FrenchBadge from "@/components/FrenchBadge";
import gustoLogo from "@/assets/gusto-logo.png";
import { APP_VERSION, LAST_UPDATE_DATE } from "@/config/version";

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showProMenu, setShowProMenu] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);

  // Check if user is a business account
  const isBusiness = user?.is_business;

  const handleLogout = () => {
    logout();
    navigate("/login");
    setShowProMenu(false);
  };

  // Display name logic
  const getDisplayName = () => {
    if (!user) return "";
    if (user.hide_identity && user.chef_alias) return user.chef_alias;
    return user.pseudo;
  };

  // Actions disponibles dans le menu + (utilisateur standard)
  const quickActions = [
    { 
      icon: CalendarPlus, 
      label: "Organiser un repas", 
      description: "Invitez et partagez les courses",
      path: "/meals/new", 
      color: "from-violet-500 to-purple-600" 
    },
    { 
      icon: ClipboardList, 
      label: "Mes repas", 
      description: "Suivi de vos repas planifiés",
      path: "/my-meals", 
      color: "from-indigo-500 to-violet-600" 
    },
    { 
      icon: Star, 
      label: "Mes notes reçues", 
      description: "Avis de vos convives",
      path: "/my-ratings", 
      color: "from-amber-400 to-yellow-500" 
    },
    { 
      icon: UtensilsCrossed, 
      label: "Nouvelle recette", 
      description: "Créez votre recette",
      path: "/record", 
      color: "from-primary to-red-500" 
    },
    { 
      icon: Receipt, 
      label: "Scanner ticket", 
      description: "Scannez vos tickets de caisse",
      path: "/my-receipts", 
      color: "from-amber-500 to-orange-600" 
    },
    { 
      icon: Refrigerator, 
      label: "Scanner frigo", 
      description: "Trouvez quoi cuisiner",
      path: "/fridge-scanner", 
      color: "from-cyan-500 to-blue-600" 
    },
    { 
      icon: UtensilsCrossed, 
      label: "Scanner plat", 
      description: "Calories et recette",
      path: "/dish-scanner", 
      color: "from-purple-500 to-pink-600" 
    },
    { 
      icon: Wine, 
      label: "Ajouter une bouteille", 
      description: "Ajoutez un vin à votre cave",
      path: "/wine-cellar?action=add", 
      color: "from-red-600 to-rose-700" 
    },
    { 
      icon: Users, 
      label: "Mes amis", 
      description: "Gérez vos amis et préférences",
      path: "/friends", 
      color: "from-blue-500 to-indigo-600" 
    },
    { 
      icon: Trophy, 
      label: "Mes points", 
      description: "Points et récompenses",
      path: "/points", 
      color: "from-amber-500 to-orange-600" 
    },
    { 
      icon: Medal, 
      label: "Classement des Chefs", 
      description: "Top des meilleurs cuisiniers",
      path: "/leaderboard", 
      color: "from-yellow-500 to-amber-600" 
    },
    { 
      icon: Store, 
      label: "Espace boutique", 
      description: "Inscrivez votre commerce",
      path: "/business/register", 
      color: "from-emerald-500 to-teal-500" 
    },
  ];

  // Actions spécifiques pour les comptes business
  const businessQuickActions = [
    { icon: Package, label: "Ajouter un produit", description: "Nouveau produit au catalogue", path: "/business/catalog", color: "from-emerald-500 to-teal-600" },
    { icon: ShoppingBag, label: "Commandes", description: "Gérer vos commandes", path: "/business/orders", color: "from-blue-500 to-indigo-600" },
    { icon: Megaphone, label: "Nouvelle campagne", description: "Créer une campagne promo", path: "/business/campaigns/new", color: "from-violet-500 to-purple-600" },
    { icon: Users, label: "Mes clients", description: "Voir la liste des clients", path: "/business/customers", color: "from-cyan-500 to-blue-600" },
    { icon: Calendar, label: "Créneaux retrait", description: "Gérer vos créneaux Click & Collect", path: "/business/pickup-slots", color: "from-amber-500 to-orange-600" },
    { icon: Settings, label: "Paramètres boutique", description: "Modifier mon profil pro", path: "/business/profile", color: "from-gray-500 to-gray-600" },
  ];

  const activeActions = isBusiness ? businessQuickActions : quickActions;

  const handleActionClick = (path) => {
    setShowActionMenu(false);
    if (!user) {
      navigate("/login");
    } else {
      navigate(path);
    }
  };

  // Navigation pour les comptes Pro (Espace Business)
  const businessNavItems = [
    { path: "/business/dashboard", icon: Home, label: "Tableau de bord", shortLabel: "Dashboard" },
    { path: "/business/orders", icon: ShoppingBag, label: "Commandes", shortLabel: "Commandes" },
    { path: "/business/campaigns/new", icon: Plus, label: "Campagne", isMain: true },
    { path: "/business/catalog", icon: Package, label: "Produits", shortLabel: "Produits" },
    { path: "/business/customers", icon: Users, label: "Clients", shortLabel: "Clients" },
  ];

  // Navigation pour les utilisateurs standard
  const userNavItems = [
    { path: "/", icon: Home, label: "Accueil", shortLabel: "Accueil" },
    { path: "/discover", icon: Compass, label: "Découvrir", shortLabel: "Découvrir" },
    { path: "/record", icon: Plus, label: "Créer", isMain: true, auth: true },
    { path: "/my-recipes", icon: BookOpen, label: "Mes Recettes", shortLabel: "Recettes", auth: true },
    { path: "/profile", icon: User, label: "Profil", shortLabel: "Profil", auth: true },
  ];

  const navItems = isBusiness ? businessNavItems : userNavItems;

  const handleNavClick = (item, e) => {
    if (item.auth && !user) {
      e.preventDefault();
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header - Always visible */}
      <header className={`fixed top-0 left-0 right-0 z-40 safe-area-top ${isBusiness ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-200' : 'glass'}`}>
        <div className="px-4 py-3 flex items-center justify-between max-w-7xl mx-auto">
          <Link to={isBusiness ? "/business/dashboard" : "/"} className="flex items-center gap-2">
            <img src={gustoLogo} alt="Gusto" className="h-9 w-auto" />
            <span className="text-[10px] font-medium text-muted-foreground/60 hidden sm:block">v{APP_VERSION}</span>
            {isBusiness && (
              <span className="font-heading text-sm font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-md tracking-wide" data-testid="pro-badge">Pro</span>
            )}
          </Link>
          <div className="flex items-center gap-3">
            {user && <NotificationBell />}
            {user && isBusiness ? (
              /* Menu déroulant pour l'espace Pro */
              <div className="relative">
                <button
                  onClick={() => setShowProMenu(!showProMenu)}
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity px-2 py-1 rounded-lg hover:bg-emerald-100"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                    <Store className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm text-emerald-700 hidden sm:block font-medium">{user.pseudo}</span>
                  <ChevronDown className={`w-4 h-4 text-emerald-600 transition-transform ${showProMenu ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown menu */}
                {showProMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowProMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="font-medium text-charcoal">{user.pseudo}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                      <Link
                        to="/business/profile"
                        onClick={() => setShowProMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
                      >
                        <Settings className="w-4 h-4 text-gray-500" />
                        <span className="text-sm">Paramètres boutique</span>
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 transition-colors text-red-600"
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm font-medium">Se déconnecter</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : user ? (
              <Link 
                to="/profile"
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <ChefAvatar
                  src={user.avatar}
                  name={user.pseudo}
                  size="sm"
                  showToque={true}
                />
                <span className="text-sm text-muted-foreground hidden sm:block">{getDisplayName()}</span>
              </Link>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-charcoal transition-colors hidden sm:block">
                  Connexion
                </Link>
                <Link
                  to="/register"
                  className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-sm font-medium px-4 py-2 rounded-full transition-colors shadow-sm"
                  data-testid="header-register-btn"
                >
                  <UserPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">Créer mon compte gratuitement</span>
                  <span className="sm:hidden">S'inscrire</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-16 pb-nav min-h-screen">
        <Outlet />
        
        {/* Footer */}
        <footer className="text-center py-6 text-xs text-muted-foreground/60 border-t border-gray-100 mt-8 mx-4">
          <p>Gusto v{APP_VERSION} • Mis à jour le {new Date(LAST_UPDATE_DATE).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          <p className="mt-1">© 2026 Gusto. Tous droits réservés.</p>
        </footer>
      </main>

      <FrenchBadge />
      <PWAInstallBanner />

      {/* Bottom Navigation */}
      <nav className={`fixed bottom-0 left-0 right-0 z-40 safe-area-bottom ${isBusiness ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-t border-emerald-200' : 'glass'}`} data-testid="bottom-navigation">
        <div className="grid grid-cols-5 items-center px-2 sm:px-4 py-2 max-w-lg mx-auto">
          {navItems.map((item, index) => {
            const isActive = location.pathname === item.path || 
              (item.path === "/business/dashboard" && location.pathname === "/business/dashboard") ||
              (item.path === "/business/orders" && location.pathname.startsWith("/business/orders")) ||
              (item.path === "/business/catalog" && location.pathname.startsWith("/business/catalog")) ||
              (item.path === "/business/customers" && location.pathname.startsWith("/business/customers"));
            const Icon = item.icon;
            
            if (item.isMain) {
              return (
                <div key={item.path} className="flex justify-center">
                  <button
                    onClick={() => setShowActionMenu(true)}
                    className="relative -mt-6 sm:-mt-8"
                    data-testid="main-action-button"
                  >
                    <motion.div
                      whileTap={{ scale: 0.95 }}
                      className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full text-white shadow-2xl flex items-center justify-center ring-4 ${
                        isBusiness 
                          ? "bg-gradient-to-r from-emerald-500 to-teal-500 ring-emerald-500/20" 
                          : "bg-primary ring-primary/20"
                      }`}
                    >
                      <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
                    </motion.div>
                  </button>
                </div>
              );
            }
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={(e) => handleNavClick(item, e)}
                className={`flex flex-col items-center justify-center py-1 sm:py-2 rounded-xl transition-colors ${
                  isActive 
                    ? (isBusiness ? "text-emerald-600" : "text-primary") 
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                <span className="text-[10px] sm:text-xs mt-0.5 sm:mt-1 font-medium truncate max-w-full">
                  {item.shortLabel || item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Action Menu Modal */}
      <AnimatePresence>
        {showActionMenu && (
          <>
            {/* Backdrop with blur */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowActionMenu(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-md z-50"
            />
            
            {/* Container: flex center on desktop, align bottom on mobile */}
            <div
              className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center pointer-events-none"
            >
              {/* Menu - Bottom sheet on mobile, centered modal on desktop */}
              <motion.div
                initial={{ opacity: 0, y: 100, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 100, scale: 0.95 }}
                transition={{ type: "spring", damping: 28, stiffness: 350 }}
                className="pointer-events-auto w-full md:w-[520px]
                  bg-white rounded-t-3xl md:rounded-3xl shadow-2xl 
                  max-h-[80vh] md:max-h-[75vh]
                  overflow-hidden"
              >
              {/* Handle - mobile only */}
              <div className="flex justify-center pt-3 pb-2 md:hidden">
                <div className="w-12 h-1.5 rounded-full bg-gray-300" />
              </div>
              
              {/* Header */}
              <div className="flex items-center justify-between px-6 pb-3 pt-2 md:pt-5">
                <h2 className="font-heading text-xl font-bold text-charcoal">Que voulez-vous faire ?</h2>
                <button
                  onClick={() => setShowActionMenu(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              {/* Actions Grid - list on mobile, 2-col grid on desktop */}
              <div className="px-4 pb-8 md:pb-6 max-h-[60vh] md:max-h-[58vh] overflow-y-auto">
                <div className="space-y-2 md:space-y-0 md:grid md:grid-cols-2 md:gap-3">
                  {activeActions.map((action, index) => (
                    <motion.button
                      key={action.path}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.04 }}
                      onClick={() => handleActionClick(action.path)}
                      className="w-full flex items-center gap-3 p-3 md:p-4 rounded-2xl 
                        bg-gray-50 hover:bg-gray-100 md:hover:scale-[1.02] md:hover:shadow-md
                        transition-all text-left"
                    >
                      <div className={`w-11 h-11 md:w-12 md:h-12 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center flex-shrink-0`}>
                        <action.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-charcoal text-sm md:text-base">{action.label}</p>
                        <p className="text-xs md:text-sm text-muted-foreground truncate">{action.description}</p>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout;
